import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/i18n/LanguageProvider'
import { MAIN_NAV_ITEMS } from '@/config/mainNav'

interface SideMenuProps {
  isOpen: boolean
  onClose: () => void
}

function navPillClass(accent: 'pink' | 'gold') {
  if (accent === 'pink') {
    return 'border border-rose-400/50 text-rose-100 bg-rose-950/40 hover:bg-rose-900/50'
  }
  return 'border border-[#B9AE9A]/45 text-[#B9AE9A] bg-[#B9AE9A]/5 hover:bg-[#B9AE9A]/15'
}

const SideMenu = ({ isOpen, onClose }: SideMenuProps) => {
  const { t } = useLanguage()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', onKey)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
            className="fixed top-0 right-0 bottom-0 w-[min(100%,22rem)] bg-[#1C1C1C] border-l border-[#B9AE9A]/15 z-[101] shadow-2xl flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#B9AE9A]/10">
              <span className="text-[#B9AE9A] font-medium tracking-wide">PathIN</span>
              <button
                type="button"
                onClick={onClose}
                className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/5"
                aria-label={t('nav.close_menu')}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-4 py-6 flex flex-col gap-3">
              {MAIN_NAV_ITEMS.map((item) => (
                <Link
                  key={item.to + item.labelKey}
                  to={item.to}
                  onClick={onClose}
                  className={`block rounded-lg px-4 py-3 text-sm font-medium tracking-wide transition-colors ${navPillClass(item.accent)}`}
                >
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default SideMenu
